import React, { useCallback, useMemo } from 'react';
import { selectDepartmentsById } from 'modules/employmentInfo/store/department/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { FormikHelpers } from 'formik';
import { EditTimeEntryPure } from 'shared/components/sidebars/EntryEdit/EditTimeEntryPure';
import { ITimeEntry, TimeEntryDataBackend } from 'shared/models/sheet/Sheet';
import { useSplitCustomFieldValueIdsByCustomFiledId } from 'store/entities/customFields/utils';
import { removeTimeEntry, updateTimeEntry } from 'store/entities/timesheet/actions/timeActions';
import {
    ITimeEntryUpdate,
    transformBackendTimeToTimeData,
    transformTimeDataToBackend,
} from 'store/entities/timesheet/models/Entry';
import { selectSheet } from 'store/entities/timesheet/selectors';
import { useTimeEntryValidationSchema } from 'shared/models/validationSchemes/timeEntry';
import {
    selectActivity,
    selectJobNumbersById,
    selectLocationsById,
    selectPositionsById,
    selectProjectsWithAssignments,
} from 'store/entities/configuration/configurationSelectors';
import { ITimeEntryFormValues } from 'shared/components/forms/entries/TimeEntryModel';
import { completesByNotes } from 'shared/utils/counters/completesCounter';
import { TSubmitBlock } from './models';
import { getProjectAssignmentByProjectAndAssignmentIds, useGetCommonEntryModel } from 'shared/utils/helpers/entries';
import { IWithInputFields } from 'shared/components/forms/utils';
import { IPayPeriod } from 'store/entities/timesheet/models/PayPeriod';
import { StatusNames } from 'store/entities/timesheet/models/Status';

interface IEditTimeEntryProps extends IWithInputFields {
    entry: ITimeEntry;
    totalMinutesByDay: Record<string, number>;
    supervisorId?: string;
    userId: string;
    SubmitBlock: TSubmitBlock;
    payPeriod: IPayPeriod;
    statusName?: StatusNames;
}

export default function EditTimeEntry({
    entry,
    supervisorId,
    userId,
    SubmitBlock,
    inputs,
    payPeriod,
    statusName,
}: IEditTimeEntryProps) {
    const dispatch = useDispatch();
    const entryId = entry.id;
    const areaId = useSelector(selectSheet(entry.sheet_id))?.area_id;
    const activity = useSelector(selectActivity(entry.activity_id));
    const positionsById = useSelector(selectPositionsById);
    const locationsById = useSelector(selectLocationsById);
    const departmentsById = useSelector(selectDepartmentsById);
    const jobNumbersById = useSelector(selectJobNumbersById);
    const projectAssignments = useSelector(selectProjectsWithAssignments);
    const schema = useTimeEntryValidationSchema(statusName, userId, entryId);
    const initialCustomFieldValues = useSplitCustomFieldValueIdsByCustomFiledId(entry.custom_field_value_ids);
    const getCommonEntryModel = useGetCommonEntryModel(userId);

    const completes = completesByNotes(entry.notes || '');
    const initialValues: ITimeEntryFormValues = useMemo(() => {
        const projectAssignment = getProjectAssignmentByProjectAndAssignmentIds(
            projectAssignments,
            entry.project_id,
            entry.assignment_id,
        );
        return {
            projectAssignment,
            taskId: entry.task_id,
            activity: activity,
            entry_date: entry.entry_date,
            data: transformBackendTimeToTimeData(entry.data as TimeEntryDataBackend),
            notes: entry.notes || '',
            completes: completes > 0 ? completes : undefined,
            scaZone: entry.sca_zone,
            position: positionsById[entry.position_id || ''],
            location: locationsById[entry.location_id || ''],
            department: departmentsById[entry.department_id || ''],
            jobNumber: jobNumbersById[entry.job_number_id || ''],
            customFieldValues: initialCustomFieldValues,
        };
    }, [
        activity,
        completes,
        departmentsById,
        entry,
        locationsById,
        positionsById,
        projectAssignments,
        jobNumbersById,
        initialCustomFieldValues,
    ]);

    const onSubmit = useCallback((values: ITimeEntryFormValues, formikActions: FormikHelpers<ITimeEntryFormValues>) => {
        const baseEntryModel = getCommonEntryModel(values);
        if (baseEntryModel && values.data) {
            const updatedEntry: ITimeEntryUpdate = {
                ...baseEntryModel,
                id: entryId,
                user_id: userId,
                data: transformTimeDataToBackend(values.data, values.entry_date),
                sca_zone: values.projectAssignment && values.projectAssignment.sca_zone_id ? values.scaZone : null,
            };
            dispatch(updateTimeEntry.init(updatedEntry));
        }
        formikActions.setSubmitting(false);
    }, [dispatch, entryId, getCommonEntryModel, userId]);

    const onDelete = useCallback(() => {
        dispatch(removeTimeEntry.init(entryId));
    }, [dispatch, entryId]);

    return (
        <EditTimeEntryPure
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={onSubmit}
            onDelete={onDelete}
            supervisorId={supervisorId}
            // @ts-ignore
            areaId={areaId}
            userId={userId}
            sheetId={entry.sheet_id}
            inputs={inputs}
            SubmitBlock={SubmitBlock}
            payPeriod={payPeriod}
        />
    );
}
